



























































import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import { PaneMoveInfo, WindowInfo } from "@/@types/window";
import ResizeKnob from "../window/ResizeKnob.vue";
import TaskManager, { MouseMoveParam } from "../task/TaskManager";
import {
  calcWindowPosition,
  createPoint,
  createRectangle,
  createSize,
  getEventPoint,
  getRightPaneRectangle
} from "../utility/CoordinateUtility";
import { getCssPxNum } from "../css/Css";
import { Point, Rectangle } from "address";
import TaskProcessor from "../task/TaskProcessor";
import { Task, TaskResult } from "task";
import TitleIcon from "../window/TitleIcon.vue";
import LifeCycle from "@/app/core/decorator/LifeCycle";

@Component({
  components: { TitleIcon, ResizeKnob }
})
export default class PaneFrame extends Vue {
  @Prop({ type: Object, required: true })
  private windowInfo!: WindowInfo<unknown>;
  @Prop({ type: String, required: true })
  private status!: string;
  @Prop({ type: Boolean, required: true })
  protected isResizing!: boolean;

  private isMounted: boolean = false;
  private dragFrom: Point = createPoint(0, 0);
  private diff: Point = createPoint(0, 0);
  private paneRectangle: Rectangle = createRectangle(0, 0, 0, 0);

  @LifeCycle
  private mounted() {
    this.isMounted = true;
    this.setMinWidth();
  }
  private get key(): string {
    return `right-pane-${this.windowInfo.key}`;
  }

  /**
   * マウス左ボタン押下イベント処理
   */
  private leftDown(event: MouseEvent | TouchEvent) {
    TaskManager.instance.setTaskParam<MouseMoveParam>("mouse-moving-finished", {
      key: this.key,
      type: null
    });
    TaskManager.instance.setTaskParam<MouseMoveParam>(
      "mouse-move-end-left-finished",
      {
        key: this.key,
        type: null
      }
    );
    this.dragFrom = getEventPoint(event);
    this.paneRectangle = this.paneElm.getBoundingClientRect() as Rectangle;
  }

  /**
   * マウス左ドラッグ中移動イベント処理
   * @param task
   * @param param
   */
  @TaskProcessor("mouse-moving-finished")
  private async mouseMoveFinished(
    task: Task<Point, never>,
    param: MouseMoveParam
  ): Promise<TaskResult<never> | void> {
    if (!param || param.key !== this.key) return;
    const point = task.value!;

    // 移動
    this.diff.x = point.x - this.dragFrom.x;
    this.diff.y = point.y - this.dragFrom.y;

    const newPoint = createPoint(
      point.x - (this.dragFrom.x - this.paneRectangle.x),
      point.y - (this.dragFrom.y - this.paneRectangle.y)
    );

    const isRight = this.windowInfo.status.indexOf("right") > -1;
    const isLeft = this.windowInfo.status.indexOf("left") > -1;
    this.windowInfo.x = newPoint.x;
    this.windowInfo.y = newPoint.y;

    if (isRight && point.x < this.paneRectangle.x) {
      // 範囲外に出たので除外中にする
      this.windowInfo.status = "right-pane-window";
      await TaskManager.instance.ignition<string, never>({
        type: "pane-relocation",
        owner: "Quoridorn",
        value: this.windowInfo.key
      });
    } else if (
      isLeft &&
      point.x > this.paneRectangle.x + this.paneRectangle.width
    ) {
      // 範囲外に出たので除外中にする
      this.windowInfo.status = "left-pane-window";
      await TaskManager.instance.ignition<string, never>({
        type: "pane-relocation",
        owner: "Quoridorn",
        value: this.windowInfo.key
      });
    } else {
      // 範囲内
      await TaskManager.instance.ignition<PaneMoveInfo, never>({
        type: isRight ? "right-pane-frame-moving" : "left-pane-frame-moving",
        owner: "Quoridorn",
        value: {
          point: newPoint,
          windowKey: this.windowInfo.key
        }
      });
    }

    task.resolve();
  }

  /**
   * マウス左ボタンドラッグ終了イベント処理
   * @param task
   * @param param
   */
  @TaskProcessor("mouse-move-end-left-finished")
  private async mouseMoveEndLeftFinished(
    task: Task<Point, never>,
    param: MouseMoveParam
  ): Promise<TaskResult<never> | void> {
    if (param && param.key === this.key) {
      const point = task.value!;

      this.windowInfo.x = point.x - (this.dragFrom.x - this.paneRectangle.x);
      this.windowInfo.y = point.y - (this.dragFrom.y - this.paneRectangle.y);

      this.dragFrom.x = 0;
      this.dragFrom.y = 0;
      this.diff.x = 0;
      this.diff.y = 0;

      // 移動によって子画面化の予備軍だった場合は子画面化する
      if (this.windowInfo.status.endsWith("-window")) {
        this.windowInfo.status = "window";
        await TaskManager.instance.ignition<string, never>({
          type: "pane-relocation",
          owner: "Quoridorn",
          value: this.windowInfo.key
        });
      }

      // 移動によって子画面化の予備軍だった場合は子画面化する
      if (this.windowInfo.status.endsWith("moving")) {
        const isRight = this.windowInfo.status.indexOf("right") > -1;
        this.windowInfo.status = isRight ? "right-pane" : "left-pane";
        setTimeout(async () => {
          await TaskManager.instance.ignition<string, never>({
            type: "pane-relocation",
            owner: "Quoridorn",
            value: this.windowInfo.key
          });
        });
      }
    }

    TaskManager.instance.setTaskParam("mouse-moving-finished", null);
    TaskManager.instance.setTaskParam("mouse-move-end-left-finished", null);

    task.resolve();
  }

  /**
   * 閉じるイベント
   */
  private async closeWindow(): Promise<void> {
    await TaskManager.instance.ignition<string, never>({
      type: "window-close",
      owner: "Quoridorn",
      value: this.windowInfo.key
    });
  }

  /**
   * 最小化イベント
   */
  private async minimizeWindow() {
    this.windowInfo.x = getRightPaneRectangle().x;
    this.windowInfo.y =
      this.paneElm.getBoundingClientRect().top + getRightPaneRectangle().y;
    await TaskManager.instance.ignition<string, never>({
      type: "window-minimize",
      owner: "Quoridorn",
      value: this.windowInfo.key
    });
  }

  /**
   * 子画面化イベント
   */
  private async normalizeWindow() {
    // 現在のサイズのまま、初期配置場所に設置しなおす
    const size = createSize(this.windowInfo.widthPx, this.windowInfo.heightPx);
    const point = calcWindowPosition(
      this.windowInfo.declare.position,
      size,
      getCssPxNum("--menu-bar-height")
    );
    this.windowInfo.x = point.x;
    this.windowInfo.y = point.y;

    this.windowInfo.status = "window";
    await TaskManager.instance.ignition<string, never>({
      type: "pane-relocation",
      owner: "Quoridorn",
      value: this.windowInfo.key
    });
  }

  private get paneElm(): HTMLDivElement {
    return this.$refs.paneFrame as HTMLDivElement;
  }

  @Emit("changeMinMaxWidth")
  @Watch("windowInfo.declare.minSize.widthPx")
  private onChangeMinWidth() {
    this.setMinWidth();
  }

  @Emit("changeMinMaxWidth")
  @Watch("windowInfo.declare.maxSize.width")
  private onChangeMaxWidth(width: number) {
    if (this.windowInfo.widthPx <= width) return;
    this.windowInfo.widthPx = width;
  }

  @Watch("isMounted")
  @Watch("windowInfo.paneY")
  private onChangeWindowY() {
    if (!this.isMounted) return;
    const y = this.windowInfo.paneY;
    this.paneElm.style.setProperty("--paneY", `${y}px`);
  }

  private setMinWidth() {
    if (!this.windowInfo.declare.minSize) return;
    const widthPx = this.windowInfo.declare.minSize.widthPx;
    this.paneElm.style.setProperty("--min-width", `${widthPx}px`);
  }
}
